// Themis permissions a patron satisfies by being somewhere physically.
const LOCATION_PERMISSIONS = ['ThemisPrmAffiliate', 'ThemisPrmFhlc']

// Any patron with a FamilySearch account satisfies this one, from anywhere.
const REGISTERED_PATRON_PERMISSION = 'ThemisPrmRegisteredPatron'

/**
 * Decides whether the limited-access alert banner should show for an image.
 *
 * Themis permissions are OR'd: a patron only needs to qualify for one of them to see the image. An
 * image carrying `ThemisPrmRegisteredPatron` is therefore viewable from home by anyone signed in, so
 * calling it "limited access" reads as a bug - patrons see the label on an image they can already
 * open, and affiliate librarians see it as evidence that affiliate access is broken when it isn't.
 *
 * Anonymous patrons never reach this check on such an image: ViewManager sends them to sign in
 * unless the permission string also has `ThemisPrmNotSignedIn`, which makes the image fully public
 * and the banner moot either way.
 *
 * @param {Object} params
 * @param {string|null} params.imagePermission - Colon-delimited DAS permission string for the image.
 * @param {boolean} params.inAffiliateLibrary - Whether the patron is at an affiliate library.
 * @param {boolean} params.inDiscoveryCenter - Whether the patron is at a discovery center.
 * @returns {boolean} True when the banner should render.
 */
export default function shouldShowLimitedAccessBanner({ imagePermission, inAffiliateLibrary, inDiscoveryCenter }) {
  if (!imagePermission) return false

  const granted = new Set(imagePermission.split(':'))
  if (granted.has(REGISTERED_PATRON_PERMISSION)) return false
  if (!inAffiliateLibrary && !inDiscoveryCenter) return false

  return LOCATION_PERMISSIONS.some((permission) => granted.has(permission))
}
